import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Box,
  Menu,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Card,
  CardContent,
  Breadcrumbs,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Tooltip,
  Switch,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../axiosInstance";
import EditIcon from "@mui/icons-material/Edit";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { BUTTON_STYLES, CARD_STYLES } from "../styles/componentStyles";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import FilterBar from "./Reusable/FilterBar";
import { toast, ToastContainer } from "react-toastify";

// Column config and visibility state
const allColumns = [
  {
    key: "bookId",
    label: "Book Id",
    filterable: true,
    cellCss: { align: "center", minWidth: "80px" },
    getValue: (r) => r.bookId,
  },
  {
    key: "courseId",
    label: "Course Id",
    filterable: true,
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.courseId,
  },
  {
    key: "semesterId",
    label: "Semester Id",
    filterable: true,
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.semesterId,
  },
  {
    key: "regulationId",
    label: "Regulation Id",
    filterable: true,
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.regulationId,
  },
  {
    key: "universityId",
    label: "University Id",
    filterable: true,
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.universityId,
  },
  {
    key: "subjectCategoryId",
    label: "SubjectCategory Id",
    filterable: true,
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.subjectCategoryId,
  },
  {
    key: "groupSubjectId",
    label: "Group Subject Id",
    filterable: true,
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.groupSubjectId,
  },

  {
    key: "bookTitle",
    label: "Book Title",
    filterable: true,
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.bookTitle,
  },
  {
    // course_books.price; null means the book's own price applies
    key: "price",
    label: "Price",
    filterable: false,
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) =>
      r.price != null ? `₹${Number(r.price).toLocaleString("en-IN")}` : "—",
  },
  {
    key: "universityName",
    label: "University Name",
    filterable: true,

    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.universityName,
  },
  {
    key: "courseName",
    label: "Course Name",
    filterable: true,
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.courseName,
  },
  {
    key: "semesterName",
    label: "Semester Name",
    filterable: true,
    cellCss: { align: "center", minWidth: "140px" },
    getValue: (r) => r.semesterName,
  },
  {
    key: "regulationName",
    label: "Regulation",
    filterable: true,
    cellCss: { align: "center", minWidth: "140px" },
    getValue: (r) => r.regulationName,
  },
  {
    key: "subjectCategoryName",
    label: "Subject Category Name",
    filterable: true,
    cellCss: { align: "center", minWidth: "200px" },
    getValue: (r) => r.subjectCategoryName,
  },
  {
    key: "groupSubjectName",
    label: "Group Subject Name",
    filterable: true,
    cellCss: { align: "center", minWidth: "200px" },
    getValue: (r) => r.groupSubjectName,
  },
];
const defaultVisibleKeys = new Set([
  "bookId",
  "bookTitle",
  "price",
  "universityName",
  "regulationId",
  "courseName",
  "semesterName",
  "regulationName",
  "subjectCategoryName",
  "groupSubjectName",
]);

const STORAGE_KEY = "couponsVisibleColumns";

const CourseBookTable = ({ onEdit, refresh, handleNew, updatedRow }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem("searchQuery") || "";
  });
  const [regulations, setRegulations] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("publisherName");
  const [order, setOrder] = useState("asc");
  const [titleSearch, setTitleSearch] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [universitySearch, setUniversitySearch] = useState("");
  const [semesterSearch, setSemesterSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [universityId, setUniversityId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [semesterId, setSemesterId] = useState("");
  const [regulationId, setRegulationId] = useState("");
  const [booksId, setBooksId] = useState([]);
  const [allbooks, setAllBooks] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [isFiltersLoadeds, setisFiltersLoadeds] = useState(false);
  const lastRefreshRef = useRef(refresh);
  // Regulation mapping with regulationId in course book mapping
  const regulationMap = new Map(
    regulations.map((reg) => [reg.id, reg.regulationName]),
  );

  // Sorting logic
  const handleSort = (key) => {
    let isAsc = orderBy === key && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(key);
  };

  // When sorting your data:
  const sortedData = [...rows].sort((a, b) => {
    let aValue, bValue;

    if (orderBy === "regulation") {
      aValue = regulationMap.get(a.regulationId) || "No Regulation";
      bValue = regulationMap.get(b.regulationId) || "No Regulation";
    } else {
      aValue = a[orderBy];
      bValue = b[orderBy];
    }

    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });
  // padding Dense
  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };

  //Memoize query params to avoid unnecessary re-renders
  const courseMappingQuery = useMemo(
    () => ({
      page,
      rowsPerPage,
      booksId,
      universityId,
      courseId,
      regulationId,
      semesterId,
      isFiltersLoadeds,
      refresh, // toggled by the page after the mapping modal closes
    }),
    [
      page,
      rowsPerPage,
      booksId,
      universityId,
      courseId,
      regulationId,
      semesterId,
      isFiltersLoadeds,
      refresh,
    ],
  );
  useEffect(() => {
    if (!isFiltersLoadeds) return;
    // A reload after the mapping modal closes runs in the background: the current
    // rows stay on screen and are replaced when the response arrives. Initial,
    // filter and paging loads still show the loading state.
    const isModalRefresh = lastRefreshRef.current !== refresh;
    lastRefreshRef.current = refresh;
    let ignore = false;
    if (!isModalRefresh) setIsLoading(true);
    setError(null);

    api
      .get("/api/course-book-mappings", {
        params: {
          page: page,
          size: rowsPerPage,
          bookId: booksId,
          universityId: universityId,
          courseId: courseId,
          regulationId: regulationId,
          semesterId: semesterId,
        },
      })
      .then((res) => {
        if (ignore) return;
        setRows(res.data.content);
        setTotalElements(res.data.totalElements);
      })
      .catch((err) => {
        if (ignore) return;
        const message =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong while fetching messages";
        setError(message);
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });
    // A newer query supersedes this one, so its late response must not overwrite rows
    return () => {
      ignore = true;
    };
  }, [courseMappingQuery]);

  // Replace an edited row in place with the saved mapping, keeping its position
  useEffect(() => {
    if (!updatedRow) return;
    const { saved, original } = updatedRow;
    const keys = ["courseId", "semesterId", "bookId", "regulationId"];
    setRows((prev) =>
      prev.map((r) =>
        keys.every((k) => String(r[k]) === String(original[k])) ? saved : r
      )
    );
  }, [updatedRow]);
  // regulation api fetching
  useEffect(() => {
    api
      .get("/api/regulation-years")
      .then((res) => setRegulations(res.data.data))
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong while fetching messages";
        setError(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  //Memoize query params to avoid unnecessary re-renders
  const queryParams = useMemo(
    () => ({
      universityId,
      courseId,
      regulationId,
      semesterId,
    }),
    [universityId, courseId, regulationId, semesterId],
  );
  // all books with Filters api fetching

  useEffect(() => {
    if (!isFiltersLoadeds) return; // wait for saved filters to load

    setIsLoading(true);
    api
      .get("/api/v1/allBooks", { params: queryParams })
      .then((res) => setAllBooks(res.data.data))
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong while fetching messages";
        setError(message);
      })
      .finally(() => setIsLoading(false));
  }, [queryParams, isFiltersLoadeds]);
  const handleDelete = () => {
    api
      .delete(`/api/course-book-mappings`, {
        data: {
          courseId: selectedId.courseId,
          semesterId: selectedId.semesterId,
          bookId: selectedId.bookId,
          regulationId: selectedId.regulationId,
        },
      })
      .then(() => {
        // Refresh data after deletion
        setRows((prevRows) =>
          prevRows.filter(
            (r) =>
              !(
                r.courseId === selectedId.courseId &&
                r.semesterId === selectedId.semesterId &&
                r.bookId === selectedId.bookId
              ),
          ),
        );
        toast.success("Successfully deleted the course mapping record.");
        setOpen(false);
        setSelectedId(null);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong while fetching messages";
        setError(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRowClick = (e, row) => {
    onEdit(row);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Load saved filters from localStorage
  const savedFilters =
    JSON.parse(localStorage.getItem("bookColumnFilters")) || {};

  // Initialize column filters
  const [columnFilters, setColumnFilters] = useState(savedFilters);

  // Tracks which filter input is open per column
  const [filterOpen, setFilterOpen] = useState(() =>
    allColumns.reduce((acc, col) => {
      acc[col.id] = savedFilters[col.id] ? true : false;
      return acc;
    }, {}),
  );
  // Load saved values from localStorage on mount
  useEffect(() => {
    const savedFilters =
      JSON.parse(localStorage.getItem("bookmapFilters")) || {}; // same key
    if (savedFilters.titleSearch) setTitleSearch(savedFilters.titleSearch);
    if (savedFilters.universitySearch)
      setUniversitySearch(savedFilters.universitySearch);
    if (savedFilters.courseSearch) setCourseSearch(savedFilters.courseSearch);
    if (savedFilters.semesterSearch)
      setSemesterSearch(savedFilters.semesterSearch);
  }, []);

  // Save to localStorage whenever any search value changes
  useEffect(() => {
    const filters = {
      titleSearch,
      courseSearch,
      universitySearch,
      semesterSearch,
    };
    localStorage.setItem("bookmapFilters", JSON.stringify(filters)); // same key
  }, [titleSearch, courseSearch, universitySearch, semesterSearch]);

  // Then filter the sortedData
  const FiletredData = sortedData.filter((row) => {
    const matchesTitle =
      !titleSearch ||
      row.bookTitle?.toLowerCase().includes(titleSearch.toLowerCase().trim());

    const matchesCourse =
      !courseSearch ||
      row.courseName?.toLowerCase().includes(courseSearch.toLowerCase().trim());

    const matchesUniversity =
      !universitySearch ||
      row.publisherName
        ?.toLowerCase()
        .includes(universitySearch.toLowerCase().trim());

    const matchesSemester =
      !semesterSearch ||
      row.semesterName
        ?.toLowerCase()
        .includes(semesterSearch.toLowerCase().trim());

    // Return true only if all conditions match
    return (
      matchesTitle && matchesCourse && matchesUniversity && matchesSemester
    );
  });

  // Initialize from localStorage synchronously to avoid a race with the persister effect
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) {
            return new Set(filtered);
          }
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Persist column config to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Columns Field showing obeject
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));
  //  Load saved filters AFTER lists are loaded

  useEffect(() => {
    const savedFilters = JSON.parse(
      localStorage.getItem("coursemappingFilters"),
    );
    if (savedFilters) {
      setBooksId(savedFilters.booksId || "");
      setUniversityId(savedFilters.universityId || "");
      setCourseId(savedFilters.courseId || "");
      setSemesterId(savedFilters.semesterId || "");
      setRegulationId(savedFilters.regulationId || "");
    }
    setisFiltersLoadeds(true);
  }, []);

  //  Save filters whenever any change happens
  useEffect(() => {
    if (!isFiltersLoadeds) return;
    const filtersToSave = {
      booksId,
      universityId,
      courseId,
      semesterId,
      regulationId,
    };
    localStorage.setItem("coursemappingFilters", JSON.stringify(filtersToSave));
  }, [
    booksId,
    universityId,
    courseId,
    semesterId,
    regulationId,
    isFiltersLoadeds,
  ]);
  // reset the Local saved filters
  const [rotating, setRotating] = useState(false);
  const handleResetFilters = () => {
    setBooksId("");
    setUniversityId("");
    setCourseId("");
    setSemesterId("");
    setRegulationId("");
    localStorage.removeItem("coursemappingFilters");
  };
  const handleClick = () => {
    setRotating(true);
    handleResetFilters?.();

    // Stop rotation after animation completes
    setTimeout(() => setRotating(false), 600); // match animation duration
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <ToastContainer />
      {/* <Card sx={{ mb: 3, boxShadow: 2, width: "100%" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                <Link underline="hover" color="inherit" to="/booklist">
                  Books List
                </Link>
                <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
                  Course Book Mapping
                </Typography>
              </Breadcrumbs>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                Book Mapping
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              disabled={isLoading}
              sx={{ minWidth: 120 }}
            >
              {isLoading ? <CircularProgress size={20} /> : "Refresh"}
            </Button>
          </Box>

          
        </CardContent>
      </Card> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // column on mobile, row on desktop
          justifyContent: { xs: "flex-start", md: "space-between" }, // start on mobile, space-between on desktop
          alignItems: { xs: "stretch", md: "center" }, // stretch on mobile, center on desktop
          gap: { xs: 1, md: 0 }, // spacing between items
          mb: 2,
          marginLeft: "10px",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" to="/booklist">
            Books List
          </Link>
          <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
            Course Book Mapping
          </Typography>
        </Breadcrumbs>

        <Box>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: { xs: "wrap", md: "nowrap" }, // wrap buttons on mobile
              mt: { xs: 1, md: 0 },
              marginRight: "10px",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenColumnsMenu}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Columns
            </Button>
            <Menu
              sx={{ maxWidth: "250px" }}
              anchorEl={columnsMenuAnchor}
              open={openColumnsMenu}
              onClose={handleCloseColumnsMenu}
            >
              {allColumns.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ pl: 1, pr: 2 }}
                />
              ))}
            </Menu>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNew}
              {...BUTTON_STYLES.primary}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Add New Mapping
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/mentorship/subjects")}
              {...BUTTON_STYLES.primary}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Mentor Subject Mapping
            </Button>
          </Stack>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
        }}
      >
        {/* <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "start",
          }}
        >
          <TextField
            size="small"
            id="outlined-basic"
            label="Search"
            variant="outlined"
            placeholder="Search Book, Course,Publisher and Semester ..."
            sx={{ width: "30%" }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ marginTop: "10px", marginLeft: "10px" }}
          >
            {FiletredData.length} of {rows.length} coupons
          </Typography>
        </Box> */}
        {/* Dense toggle */}

        <Box
          sx={{
            display: { md: "flex", xs: "block" },
            alignItems: "center",
            gap: 2,
            width: "95%",
          }}
        >
          <FilterBar
            universityId={universityId}
            courseId={courseId}
            semesterId={semesterId}
            regulationId={regulationId}
            setUniversityId={setUniversityId}
            setCourseId={setCourseId}
            setSemesterId={setSemesterId}
            setRegulationId={setRegulationId}
          />
          <Stack
            direction="row"
            sx={{
              width: { md: "16%", xs: "100%" },
              marginTop: { md: "0", xs: "10px" },
            }}
            spacing={2}
            flexWrap="wrap"
            useFlexGap
          >
            <Autocomplete
              size="small"
              fullWidth
              sx={{
                width: { xs: "100%", md: "100%" },
                mb: { xs: "10px", md: 0 },
                backgroundColor: "#fff",
              }}
              options={
                allbooks.filter(
                  (v, i, a) =>
                    a.findIndex((t) => t.id === v.id && t.title === v.title) ===
                    i,
                ) || []
              } // removes duplicates
              // getOptionLabel={(option) =>
              //   option?.id && option?.title
              //     ? `${option.id} - ${option.title}`
              //     : ""
              // }
              getOptionLabel={(option) =>
                option?.id && option?.title
                  ? `${option.id} - ${option.title}`
                  : ""
              }
              value={allbooks.find((u) => u.id === booksId) || null}
              onChange={(event, newValue) => {
                setBooksId(newValue?.id || "");
              }}
              filterOptions={(options, { inputValue }) =>
                options.filter(
                  (option) =>
                    option.id?.toString() === inputValue ||
                    option.title
                      ?.toLowerCase()
                      .includes(inputValue.toLowerCase()),
                )
              }
              renderOption={(props, option, index) => (
                <li {...props} key={`${option.id}-${option.id || index}`}>
                  {option.id} - {option.title}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Book "
                  variant="outlined"
                  placeholder="Search Book ..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading && (
                          <CircularProgress size={20} sx={{ mr: 2 }} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Stack>
          <Button
            onClick={handleClick}
            variant="outlined"
            sx={{
              display: { xs: "none", md: "flex" },
              minWidth: "20px",
              padding: "4px 4px",
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              color: "#17479e",
              borderColor: "#17479e",
              backgroundColor: "#f8f9ff",
              transition: "all 0.3s ease",
              boxShadow: "0 0 0 rgba(0,0,0,0)",
              "&:hover": {
                backgroundColor: "#17479e",
                color: "#fff",
                borderColor: "#17479e",
                transform: "scale(1.05)",
                boxShadow: "0 3px 10px rgba(25, 118, 210, 0.3)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            <RefreshIcon
              sx={{
                animation: rotating ? "spin 0.6s linear" : "none",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </Button>
          <Typography
            sx={{ display: { xs: "none", md: "block" } }}
            variant="body2"
            color="text.secondary"
          >
            {FiletredData.length} of {totalElements} Courses
          </Typography>
        </Box>
        <Tooltip
          sx={{ display: { xs: "none", md: "block" } }}
          title="Dense padding"
          arrow
        >
          <FormControlLabel
            control={
              <Switch
                checked={dense}
                onChange={handleChangeDense}
                color="primary"
              />
            }
          />
        </Tooltip>
      </Box>
      <Box
        sx={{
          display: "flex",
          textAlign: "left",
          marginLeft: "20px",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{ display: { xs: "block", md: "none" } }}
          variant="body2"
          color="text.secondary"
        >
          {FiletredData.length} of {totalElements} Course Book
        </Typography>
        <Button
          onClick={handleClick}
          variant="outlined"
          sx={{
            display: { xs: "flex", md: "none" },
            minWidth: "20px",
            padding: "4px 4px",
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            color: "#17479e",
            borderColor: "#17479e",
            backgroundColor: "#f8f9ff",
            transition: "all 0.3s ease",
            boxShadow: "0 0 0 rgba(0,0,0,0)",
            "&:hover": {
              backgroundColor: "#17479e",
              color: "#fff",
              borderColor: "#17479e",
              transform: "scale(1.05)",
              boxShadow: "0 3px 10px rgba(25, 118, 210, 0.3)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
          }}
        >
          <RefreshIcon
            sx={{
              animation: rotating ? "spin 0.6s linear" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        </Button>
        <Tooltip
          sx={{ display: { xs: "block", md: "none" } }}
          title="Dense padding"
          arrow
        >
          <FormControlLabel
            control={
              <Switch
                checked={dense}
                onChange={handleChangeDense}
                color="primary"
              />
            }
          />
        </Tooltip>
      </Box>
      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : null}
      {!isLoading && (
        <Fade in={!isLoading} timeout={500}>
          <Paper
            sx={{
              width: "98%",
              margin: "0 auto",
              overflow: "hidden",
              boxShadow: 2,
              marginBottom: "30px",
            }}
          >
            <TableContainer
              className="book-table"
              sx={{ maxHeight: "70vh" }}
            >
              <Table
                stickyHeader
                sx={{ minWidth: 650 }}
                size={dense ? "small" : "medium"}
              >
                <TableHead className="table-head">
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          align: "left",
                          backgroundColor: "#17479e",
                          color: "#ffffff",
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <Box>
                          <TableSortLabel
                            active={orderBy === c.key}
                            direction={orderBy === c.key ? order : "asc"}
                            onClick={() => handleSort(c.key)}
                            sx={{
                              color: "#ffffff !important",
                              "&.Mui-active": {
                                color: "#ffffff !important",
                              },
                              "& .MuiTableSortLabel-icon": {
                                color: "#ffffff !important",
                              },
                            }}
                          >
                            {c.label}
                          </TableSortLabel>
                          {/* {c.filterable && (
                            <IconButton
                              size="small"
                              // onClick={() => toggleFilter(c.id)}
                              sx={{ color: "#fff" }}
                            >
                              {filterOpen[c.id] ? (
                                <FilterListOffIcon sx={{ fontSize: "14px" }} />
                              ) : (
                                <FilterListIcon sx={{ fontSize: "14px" }} />
                              )}
                            </IconButton>
                          )} */}
                        </Box>
                        {filterOpen[c.id] && (
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder={`Search ${c.label}`}
                            value={columnFilters[c.id]}
                            // onChange={(e) =>
                            //   handleColumnFilterChange(
                            //     c.id,
                            //     e.target.value
                            //   )
                            // }
                            sx={{
                              mt: 1,
                              width: "100%",
                              "& .MuiInputBase-root": {
                                minHeight: "15px", // Increase input box height

                                color: "#fff", // text color inside input
                                "& fieldset": {
                                  borderColor: "#ffffffff", // border color
                                },
                                "&:hover fieldset": {
                                  borderColor: "#ffffffff", // border color on hover
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "#ffffffff", // border color when focused
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                py: 0.5, // Optional: adjust vertical padding of text
                              },
                            }}
                          />
                        )}
                      </TableCell>
                    ))}

                    {/* <TableCell
                      className="table-cell"
                      onClick={() => handleSort("regulation")}
                      sx={{
                        align: "left",
                        backgroundColor: "#17479e",
                        color: "#ffffff",
                      }}
                      sortDirection={orderBy === "regulation" ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === "regulation"}
                        direction={orderBy === "regulation" ? order : "asc"}
                        onClick={() => handleSort("regulation")}
                        sx={{
                          color: "#ffffff !important",
                          "&.Mui-active": {
                            color: "#ffffff !important",
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: "#ffffff !important",
                          },
                        }}
                      >
                        Regulation
                      </TableSortLabel>
                    </TableCell> */}

                    <TableCell
                      className="table-cell"
                      sx={{
                        align: "left",
                        backgroundColor: "#17479e",
                        color: "#ffffff",
                      }}
                    >
                      Marks (L/S)
                    </TableCell>
                    <TableCell
                      sx={{
                        align: "left",
                        backgroundColor: "#17479e",
                        color: "#ffffff",
                        minWidth: "80px",
                        position: "sticky",
                        right: 0,
                        zIndex: 3,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {FiletredData.map((row, index) => (
                    <TableRow
                      key={`${row.courseId}-${row.semesterId}-${row.bookId}-${row.regulationId}`}
                      className="table-row"
                      hover
                      style={{ cursor: "pointer" }}
                    >
                      {visibleColumnDefs.map((c) => (
                        <TableCell key={c.key} sx={{ ...c.cellCss }}>
                          {c.getValue(row)}
                        </TableCell>
                      ))}

                      {/* <TableCell className="table-cell">
                        {regulationMap.get(row.regulationId) || "No Regulation"}
                      </TableCell> */}
                      <TableCell className="table-cell">
                        {row.params ? JSON.stringify(row.params) : ""}
                      </TableCell>
                      <TableCell
                        className="table-cell"
                        sx={{
                          position: "sticky",
                          right: 0,
                          zIndex: 1,
                          backgroundColor: "#fff",
                        }}
                      >
                        <Tooltip
                          title="Edit Course Mapping"
                          arrow
                          placement="top"
                        >
                          <IconButton
                            color="primary"
                            onClick={(event) => handleRowClick(event, row)}
                            size="small"
                            sx={{
                              "&:hover": {
                                backgroundColor: "primary.light",
                                color: "white",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title="Delete Course Mapping"
                          arrow
                          placement="top"
                        >
                          <IconButton
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClickOpen(row, e);
                            }}
                            size="small"
                            sx={{
                              "&:hover": {
                                backgroundColor: "error.light",
                                color: "white",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Fade>
      )}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this coupon? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default CourseBookTable;
